import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import TextButton from '@shared/components/core/button/TextButton';
import ExpanderCard from '@shared/components/core/card/ExpanderCard';
import ContentDialog from '@shared/components/core/dialog/ContentDialog';
import ChipButtonContainer from '@shared/components/core/layouts/ChipButtonContainer';
import { StickyFooter } from '@shared/components/core/structure/CommonBodyLayoutStyles';
import { FieldLabel } from '@shared/components/core/text/FormsTypography';
import CommandField from '@shared/components/input/field/CommandField';
import CommandPickerDialog from '@shared/components/input/field/CommandPickerDialog';
import UnsavedChangesDialog from '@shared/components/loading/UnsavedChangesDialog';
import { CommandType, commandTypesDisplay } from '@shared/constants/CommandValues';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { useScrollToQueryParam } from '@shared/hooks/useScrollToQueryParam';
import { KeyCommandDisplayMapping } from '@shared/models/deviceProfiles/KeyCommandMapping';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ProfileKeybindingsPageContent() {
    const { t } = useTranslation();
    useScrollToQueryParam();
    const [keyToAddCommand, setKeyToAddCommand] = useState<KeyCommandDisplayMapping>();
    const [isResetConfirmationOpen, setIsResetConfirmationOpen] = useState<boolean>(false);
    const [resetOption, setResetOption] = useState<'default' | 'empty'>('default');
    const commandKeyMappings = useEditProfileStore((state) => state.commandKeyMappings);
    const isPreview = useEditProfileStore((state) => state.isPreview);
    const hasChanges = useEditProfileStore((state) => state.hasChanges);
    const id = useEditProfileStore((state) => state.id);
    const { saveChanges, setHasChanges, updateCommandKeyMappings } = useEditProfileStore.getState();

    const handleCommandChange = (value: string, keybind: KeyCommandDisplayMapping, index: number) => {
        const editProfileStore = useEditProfileStore.getState();
        const updatedMappings = editProfileStore.commandKeyMappings.map((k) => {
            if (k.key !== keybind.key) return k;
            return {
                ...k,
                commands: k.commands.map((c, i) => (i === index ? { ...c, command: value } : c)),
            };
        });
        setHasChanges(true);
        updateCommandKeyMappings(updatedMappings);
    };

    const handleRemoveCommand = (command: string, keybind: KeyCommandDisplayMapping, index: number) => {
        const editProfileStore = useEditProfileStore.getState();
        const updatedMappings = editProfileStore.commandKeyMappings.map((k) => {
            if (k.key !== keybind.key) return k;
            return {
                ...k,
                commands: k.commands.filter((_, i) => i !== index),
            };
        });
        editProfileStore.setHasChanges(true);
        editProfileStore.updateCommandKeyMappings(updatedMappings);
    };
    const handleAddCommand = (keybind: KeyCommandDisplayMapping) => {
        useEditProfileStore.getState().setHasChanges(true);
        setKeyToAddCommand(keybind);
    };
    const handleAddCommandWithType = (keybind: KeyCommandDisplayMapping, commandType: CommandType) => {
        setKeyToAddCommand(undefined);
        const editProfileStore = useEditProfileStore.getState();
        editProfileStore.setHasChanges(true);
        const updatedMappings = editProfileStore.commandKeyMappings.map((k) => {
            if (k.key !== keybind.key) return k;
            return {
                ...k,
                commands: [...k.commands, { command: '', commandType }],
            };
        });
        editProfileStore.updateCommandKeyMappings(updatedMappings);
    };

    const handleSave = async () => {
        const result = await useEditProfileStore.getState().saveChanges();
    };

    const handleReset = () => {
        const editProfileStore = useEditProfileStore.getState();
        if (resetOption === 'default') {
            editProfileStore.resetKeybindingsToDefault();
        } else {
            editProfileStore.resetKeybindingsToEmpty();
        }
        setIsResetConfirmationOpen(false);
        setResetOption('default');
    };

    return (
        <>
            <ChipButtonContainer>
                <TextButton
                    startIcon="repeat"
                    disabled={isPreview}
                    onClick={() => {
                        setResetOption('default');
                        setIsResetConfirmationOpen(true);
                    }}>
                    {t('common:keys.reset.title')}
                </TextButton>
            </ChipButtonContainer>
            {commandKeyMappings?.map((k, i) => (
                <ExpanderCard title={k.displayName ?? k.key} key={i} id={k.key}>
                    <FieldLabel>{t('common:macros.title')}</FieldLabel>
                    {k.commands?.map((c, i) => (
                        <CommandField
                            key={i}
                            disabled={isPreview}
                            value={c.command}
                            commandType={c.commandType}
                            startIcon={commandTypesDisplay[c.commandType].icon}
                            onChange={(value) => handleCommandChange(value, k, i)}
                            onRemove={() => handleRemoveCommand(c.command, k, i)}
                        />
                    ))}
                    <ChipButtonContainer>
                        <TextButton startIcon="plus" disabled={isPreview} onClick={() => handleAddCommand(k)}>
                            {t('common:input.add')}
                        </TextButton>
                    </ChipButtonContainer>
                </ExpanderCard>
            ))}
            <CommandPickerDialog
                open={!!keyToAddCommand}
                onClose={() => setKeyToAddCommand(undefined)}
                onCommandTypeSelected={(commandType: CommandType) => {
                    if (keyToAddCommand) {
                        handleAddCommandWithType(keyToAddCommand, commandType);
                    }
                }}
            />
            {hasChanges && (
                <UnsavedChangesDialog
                    hasChanges={hasChanges}
                    setHasChanges={setHasChanges}
                    onSave={async () => {
                        await saveChanges();
                    }}
                />
            )}
            <StickyFooter contentPosition="center" $visuallyHide={!hasChanges && id !== undefined}>
                <Button $color="primary" $size="large" startIcon="save" onClick={handleSave}>
                    {t('common:saveAndFinish')}
                </Button>
            </StickyFooter>
            <ContentDialog
                open={isResetConfirmationOpen}
                onCancel={() => {
                    setIsResetConfirmationOpen(false);
                    setResetOption('default');
                }}
                onClose={() => {
                    setIsResetConfirmationOpen(false);
                    setResetOption('default');
                }}
                title={t('common:keys.reset.confirm')}
                description={t('common:keys.reset.confirmDescription')}
                onConfirm={handleReset}
                confirmButtonText={t('common:keys.reset.confirmButton')}
                cancelButtonText={t('common:cancel')}
                confirmIcon="repeat">
                <RadioGroup
                    name="resetOption"
                    value={resetOption}
                    onChange={(e) => setResetOption(e.target.value as 'default' | 'empty')}>
                    <FormControlLabel
                        value="default"
                        control={<Radio />}
                        label={t('common:keys.reset.defaultOption')}
                    />
                    <FormControlLabel value="empty" control={<Radio />} label={t('common:keys.reset.emptyOption')} />
                </RadioGroup>
            </ContentDialog>
        </>
    );
}
